import theme from "../../theme/theme";
import { useNavigate } from "react-router-dom";
import { useCallback } from "react";
import { Typography, Avatar, Stack, Badge } from "@mui/material";
import { NotificationType, Role } from "@enums";
import { NotificationOut } from "@providers/hop-ord-server/api";
import { useCheckUserRole, dateOfBirthFormat, timeFromNow } from "@utils";
import { OrderStatus } from "@enums";
import {
  Comment as CommentIcon,
  TaskAlt as TaskAltIcon,
} from "@mui/icons-material";

const NotificationIcon = ({ type }: { type: NotificationType | null }) => {
  switch (type) {
    case NotificationType.NEW_COMMENT:
      return <CommentIcon fontSize="small" />;
    case NotificationType.ORDER_STATUS:
      return <TaskAltIcon fontSize="small" />;
    default:
      return <></>;
  }
};

interface NotificationItemProps {
  notification: NotificationOut;
  toggleDrawer: () => void;
}

const NotificationItem = ({
  notification,
  toggleDrawer,
}: NotificationItemProps) => {
  const navigate = useNavigate();
  const isRo = useCheckUserRole(Role.RO);
  const handleOrderSelect = useCallback(
    (latestOrderId: number | null) => {
      toggleDrawer();

      // Since the Order could've been edited since the Notification was created we want
      // to ensure that the Notification redirects to the most recent version of the order

      if (!latestOrderId) {
        return;
      }

      let path = `/order/${latestOrderId}`;
      if (
        latestOrderId &&
        notification.latestOrderStatus !== OrderStatus.DRAFT
      ) {
        if (isRo) {
          path = `/review/${latestOrderId}`;
        } else {
          path = `/commandcenter/${latestOrderId}/plan-review`;
        }
      }

      navigate(path, {
        state: {
          notificationType: notification.notificationType,
        },
      });
    },
    [navigate, toggleDrawer, notification, isRo],
  );

  if (!notification.orderId || !notification.orderGroupId) return <></>;

  const patId = notification.patientMrn || "-";
  const dob = dateOfBirthFormat(notification.patientDob || "");

  return (
    <Stack
      direction="row"
      padding={2}
      sx={{
        cursor: "pointer",
        borderRadius: 2,
        ":hover": {
          backgroundColor: theme.palette.action.hover,
        },
      }}
      justifyContent="space-between"
      onClick={() =>
        handleOrderSelect(notification?.latestOrderId || notification?.orderId)
      }
    >
      <Stack direction="row" gap={1} sx={{ alignItems: "flex-start" }}>
        <Badge
          variant="dot"
          invisible={!!notification.viewed}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          overlap="circular"
          slotProps={{
            badge: {
              sx: {
                outline: "2px solid",
                outlineColor: "background.paper",
              },
            },
          }}
        >
          <Avatar
            sx={{
              backgroundColor: "secondary.main",
              color: "primary.contrastText",
              width: 32,
              height: 32,
            }}
          >
            <NotificationIcon
              type={notification.notificationType as NotificationType}
            />
          </Avatar>
        </Badge>
        <Stack direction="column">
          <Typography variant="subtitle1">{notification.content}</Typography>
          <Typography variant="body1">{notification.patientName}</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {`Pat ID: ${patId} | DOB: ${dob}`}
          </Typography>
        </Stack>
      </Stack>
      <Typography variant="caption" color="grey.700">
        {timeFromNow(notification.createdAt, "date")}
      </Typography>
    </Stack>
  );
};

export default NotificationItem;
